import type { MongooseReference, PopulatedMongooseReference } from "../../../mongooseUtil"


// Anything an id can be read from: an id string, an id object, or a populated document.
export type RHReferenceLike = string | object | null | undefined


/** The populated document behind a reference, or undefined while it is only an id. */
export function populatedValueOfReference<T extends object>(
    reference: MongooseReference<T> | null | undefined
): PopulatedMongooseReference<T> | undefined {
    if (typeof reference === "object" && reference !== null && !("_bsontype" in reference)) {
        return reference
    }
    return undefined
}


/** The populated documents among a list of references; entries that are still ids are left out. */
export function populatedValuesOfReferences<T extends object>(
    references: MongooseReference<T>[] | null | undefined
): PopulatedMongooseReference<T>[] {
    const populatedValues: PopulatedMongooseReference<T>[] = []
    for (const reference of references ?? []) {
        const populatedValue = populatedValueOfReference(reference)
        if (populatedValue) {
            populatedValues.push(populatedValue)
        }
    }
    return populatedValues
}


/**
 * The id behind a reference that may be an id string, an id object or a populated document
 * (with `_id`, or the older `id`); undefined when there is none.
 */
export function referenceId(reference: RHReferenceLike): string | undefined {
    if (!reference) {
        return undefined
    }
    if (typeof reference === "string") {
        return reference
    }
    if (typeof reference === "object") {
        if ("_id" in reference && reference._id) {
            return referenceId(reference._id)
        }
        if ("id" in reference && reference.id) {
            return referenceId(reference.id)
        }
        const stringValue = reference.toString()
        if (stringValue !== "[object Object]") {
            return stringValue
        }
    }
    return undefined
}


/**
 * A plain object standing in for a populated document, e.g. file metadata the client builds from an id.
 * PopulatedMongooseReference intersects two _id types, so such an object cannot be written as a literal of it.
 */
export function populatedReferenceOf<T extends object>(value: T): PopulatedMongooseReference<T> {
    return value as PopulatedMongooseReference<T>
}
